import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  GlobeAltIcon,
  BuildingOffice2Icon,
  AcademicCapIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
  UsersIcon,
  UserIcon,
  DocumentTextIcon,
  TagIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CogIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { 
  useAuth, 
  useCanViewUniversityDropdown,
  useCanViewStudentDropdown,
  useCanViewUserManagementDropdown,
  useCanViewUniversity,
  useCanViewCountry,
  useCanViewCity,
  useCanViewUniversityProgram,
  useCanViewScholarship,
  useCanViewStudent,
  useCanViewStudentRequest,
  useCanViewContract,
  useCanViewCashManagement,
  useCanViewConsultant,
  useCanViewUserManagement,
  useCanViewRoleManagement,
  useCanViewTaskManagement
} from '../hooks/useAuth';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Sidebar({ onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  
  // Auth hooks
  const { user, logout } = useAuth();
  
  // Dropdown permission checks - strictly permission-based
  const canViewUniversityDropdown = useCanViewUniversityDropdown();
  const canViewStudentDropdown = useCanViewStudentDropdown();
  const canViewUserManagementDropdown = useCanViewUserManagementDropdown();
  
  // Specific permission checks - strictly permission-based
  const canViewUniversity = useCanViewUniversity();
  const canViewCountry = useCanViewCountry();
  const canViewCity = useCanViewCity();
  const canViewUniversityProgram = useCanViewUniversityProgram();
  const canViewScholarship = useCanViewScholarship();
  const canViewStudent = useCanViewStudent();
  const canViewStudentRequest = useCanViewStudentRequest();
  const canViewContract = useCanViewContract();
  const canViewCashManagement = useCanViewCashManagement();
  const canViewConsultant = useCanViewConsultant();
  const canViewUserManagement = useCanViewUserManagement();
  const canViewRoleManagement = useCanViewRoleManagement();
  const canViewTaskManagement = useCanViewTaskManagement();

  // Create navigation based on permissions - strictly permission-based
  const getNavigation = () => {
    const navigation = [];

    // Dashboard - always visible for authenticated users
    navigation.push({ 
      name: 'İdarə Paneli', 
      href: '/dashboard', 
      icon: HomeIcon,
      type: 'single',
      visible: true
    });

    // Universities section - only show dropdown if user has university.dropdown permission
    if (canViewUniversityDropdown) {
      const universitiesChildren = [];
      
      // Countries - only show if user has country.view permission
      if (canViewCountry) {
        universitiesChildren.push({ name: 'Ölkələr', href: '/countries', icon: GlobeAltIcon });
      }
      
      // Cities - only show if user has city.view permission
      if (canViewCity) {
        universitiesChildren.push({ name: 'Şəhərlər', href: '/cities', icon: BuildingOffice2Icon });
      }
      
      // Universities - only show if user has university.view permission
      if (canViewUniversity) {
        universitiesChildren.push({ name: 'Universitetlər', href: '/universities', icon: AcademicCapIcon });
      }
      
      // University Programs - only show if user has university_program.view permission
      if (canViewUniversityProgram) {
        universitiesChildren.push({ name: 'Ixtisaslar', href: '/university-programs', icon: BookOpenIcon });
      }
      
      // Scholarships - only show if user has scholarship.view permission
      if (canViewScholarship) {
        universitiesChildren.push({ name: 'Təqaüd Proqramları', href: '/scholarships', icon: CurrencyDollarIcon });
      }
      
      // Only add the dropdown if there are children to show
      if (universitiesChildren.length > 0) {
        navigation.push({
          name: 'Universitet İdarəetməsi',
          icon: CogIcon,
          type: 'dropdown',
          children: universitiesChildren,
          visible: true
        });
      }
    }

    // Students section - only show dropdown if user has student_dropdown permission
    if (canViewStudentDropdown) {
      const studentsChildren = [];
      
      // Students - only show if user has student.view permission
      if (canViewStudent) {
        studentsChildren.push({ name: 'Tələbələr', href: '/students', icon: UserIcon });
      }
      
      // Student Requests - only show if user has student_request.view permission
      if (canViewStudentRequest) {
        studentsChildren.push({ name: 'Müraciətlər', href: '/student-requests', icon: DocumentTextIcon });
        // studentsChildren.push({ name: 'Müraciət Statusları', href: '/request-statuses', icon: TagIcon });
      }
      
      // Contracts - only show if user has contract.view permission
      if (canViewContract) {
        studentsChildren.push({ name: 'Müqavilələr', href: '/contracts', icon: ClipboardDocumentListIcon });
      }
      
      // Only add the dropdown if there are children to show
      if (studentsChildren.length > 0) {
        navigation.push({
          name: 'Tələbə İdarəetməsi',
          icon: UserIcon,
          type: 'dropdown',
          children: studentsChildren,
          visible: true
        });
      }
    }

    // Cash Management - only show if user has cash_management.view permission
    if (canViewCashManagement) {
      navigation.push({ 
        name: 'Kassa İdarəetməsi', 
        href: '/cash-management', 
        icon: BanknotesIcon,
        type: 'single',
        visible: true
      });
    }

    // Task Management - only show if user has task_management.view permission
    if (canViewTaskManagement) {
      navigation.push({ 
        name: 'Tapşırıq İdarəetməsi', 
        href: '/task-management', 
        icon: ClipboardDocumentListIcon,
        type: 'single',
        visible: true
      });
    }

    // User Management section - only show dropdown if user has user_management.dropdown permission
    if (canViewUserManagementDropdown) {
      const userManagementChildren = [];
      
      // Consultants - only show if user has consultant.view permission
      if (canViewConsultant) {
        userManagementChildren.push({ name: 'Konsultantlar', href: '/consultants', icon: UsersIcon });
      }
      
      // User Management - only show if user has user_management.view permission
      if (canViewUserManagement) {
        userManagementChildren.push({ name: 'İstifadəçi İdarəetməsi', href: '/user-management', icon: UserGroupIcon });
      }
      
      // Role Management - only show if user has role_management.view permission
      if (canViewRoleManagement) {
        userManagementChildren.push({ name: 'Rol İdarəetməsi', href: '/role-management', icon: ShieldCheckIcon });
      }
      
      // Only add the dropdown if there are children to show
      if (userManagementChildren.length > 0) {
        navigation.push({
          name: 'İstifadəçi İdarəetməsi',
          icon: UserGroupIcon,
          type: 'dropdown',
          children: userManagementChildren,
          visible: true
        });
      }
    }

    return navigation.filter(item => item.visible);
  };

  const navigation = getNavigation();

  const handleLogout = () => {
    if (window.confirm('Çıxış etmək istədiyinizə əminsiniz?')) {
      logout();
      if (onLogout) {
        onLogout();
      }
    }
  };

  const toggleDropdown = (itemName) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const isActiveDropdown = (children) => {
    return children.some(child => location.pathname === child.href);
  };

  const renderNavigationItem = (item, isMobile = false) => {
    if (item.type === 'single') {
      return (
        <Link
          key={item.name}
          to={item.href}
          className={classNames(
            location.pathname === item.href
              ? 'bg-white bg-opacity-20 text-white border-l-4 border-white shadow-lg'
              : 'text-blue-100 hover:text-white hover:bg-white hover:bg-opacity-10',
            'group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold transition-all duration-200 transform hover:translate-x-1 hover:shadow-md'
          )}
          onClick={isMobile ? () => setSidebarOpen(false) : undefined}
        >
          <item.icon
            className={classNames(
              location.pathname === item.href ? 'text-white' : 'text-blue-200 group-hover:text-white',
              'h-6 w-6 shrink-0'
            )}
          />
          {item.name}
        </Link>
      );
    }

    const isOpen = openDropdowns[item.name];
    const isActive = isActiveDropdown(item.children);

    return (
      <div key={item.name}>
        <button
          onClick={() => toggleDropdown(item.name)}
          className={classNames(
            isActive
              ? 'bg-white bg-opacity-20 text-white border-l-4 border-white shadow-lg'
              : 'text-blue-100 hover:text-white hover:bg-white hover:bg-opacity-10',
            'group flex w-full items-center justify-between gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold transition-all duration-200 transform hover:translate-x-1 hover:shadow-md'
          )}
        >
          <div className="flex items-center gap-x-3">
            <item.icon
              className={classNames(
                isActive ? 'text-white' : 'text-blue-200 group-hover:text-white',
                'h-6 w-6 shrink-0'
              )}
            />
            {item.name}
          </div>
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-blue-200 group-hover:text-white" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-blue-200 group-hover:text-white" />
          )}
        </button>
        
        {isOpen && (
          <div className="ml-6 mt-2 space-y-1">
            {item.children.map((child) => (
              <Link
                key={child.name}
                to={child.href}
                className={classNames(
                  location.pathname === child.href
                    ? 'bg-white bg-opacity-30 text-white border-l-2 border-white'
                    : 'text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10',
                  'group flex gap-x-3 rounded-md p-2 pl-4 text-xs leading-5 font-medium transition-all duration-200'
                )}
                onClick={isMobile ? () => setSidebarOpen(false) : undefined}
              >
                <child.icon className="h-4 w-4 shrink-0" />
                {child.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  // User display
  const userDisplayName = user ? `${user.first_name} ${user.last_name}` : 'İstifadəçi';
  const userRole = user?.role?.name || 'Rol';

  return (
    <>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 ${sidebarOpen ? 'block' : 'hidden'} lg:hidden`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-blue-600 via-purple-600 to-indigo-700 shadow-xl">
          <div className="flex items-center justify-between h-16 px-4 border-b border-white border-opacity-20">
            <h1 className="text-xl font-bold text-white">Unidestin.az</h1>
            <button onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="h-6 w-6 text-white hover:text-blue-200 transition-colors" />
            </button>
          </div>
          <nav className="mt-8 px-2 space-y-2 overflow-y-auto flex-1">
            {navigation.map((item) => renderNavigationItem(item, true))}
          </nav>
          
          {/* Mobile User Section */}
          <div className="mt-8 mx-2">
            <div className="border-t border-white border-opacity-20 pt-4">
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <UserCircleIcon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{userDisplayName}</p>
                    <p className="text-xs text-blue-200 truncate">{userRole}</p>
                  </div>
                  <button 
                    className="flex-shrink-0 p-1 text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-md transition-all duration-200"
                    title="Çıxış"
                    onClick={() => {
                      handleLogout();
                      setSidebarOpen(false);
                    }}
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gradient-to-b from-blue-600 via-purple-600 to-indigo-700 px-6 pb-4 shadow-lg">
          <div className="flex h-16 shrink-0 items-center border-b border-white border-opacity-20">
            <h1 className="text-2xl font-bold text-white">Unidestin.az</h1>
          </div>
          <nav className="flex flex-1 flex-col">
            <div className="flex flex-1 flex-col gap-y-7">
              <div>
                <div className="-mx-2 space-y-2">
                  {navigation.map((item) => renderNavigationItem(item))}
                </div>
              </div>
            </div>
          </nav>
          
          {/* Sidebar Footer - Logged in User */}
          <div className="border-t border-white border-opacity-20 pt-4">
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <UserCircleIcon className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{userDisplayName}</p>
                  <p className="text-xs text-blue-200 truncate">{userRole}</p>
                </div>
                <button 
                  className="flex-shrink-0 p-1 text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-md transition-all duration-200"
                  title="Çıxış"
                  onClick={handleLogout}
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-4 shadow-lg sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-white hover:text-blue-200 transition-colors lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <div className="flex-1 text-sm font-semibold leading-6">
          <h1 className="text-xl font-bold text-white">Unidestin.az</h1>
        </div>
      </div>
    </>
  );
} 